import $ from "jquery";
import bootstrap, { Modal } from "bootstrap";
import { InCoreSwal2, InCoreSwal2Icon, InCoreSwal2Type, InCoreSwal2X } from "./swal.utils";
import { ValidationErrorToTooltip } from "./document.utils";


let _modalCloseCallback: Function | undefined;

// Guard, true mentre un'apertura modale è in corso. Impedisce che un
// doppio trigger (es. eventClick del calendario che parte due volte) avvii una seconda apertura
let _modalOpening = false;

/**
 * Standard modal actions.
 */
export const InCoreModalAction = {
    CREATE: "Create",
    DELETE: "Delete",
    DETAILS: "Details",
    EDIT: "Edit",
    INDEX: "Index",
};

/**
 * Modal layout configuration class.
 */
export class InCoreModalLayout {
    below: boolean;
    above: boolean;
    x: boolean;
    large: boolean;
    static: boolean;
    title: string;

    constructor(Large = true, Below = true, Above = false, X = false, Static = false, Title = "") {
        this.below = Below;
        this.above = Above;
        this.x = X;
        this.large = Large;
        this.static = Static;
        this.title = Title;
    }

    ButtonsBelow(Large = true, Static = false, Title = ""): this {
        this.below = true;
        this.above = false;
        this.x = false;
        this.large = Large;
        this.static = Static;
        this.title = Title;
        return this;
    }

    ButtonsBelowX(Large = true, Static = false, Title = ""): this {
        this.below = true;
        this.above = false;
        this.x = true;
        this.large = Large;
        this.static = Static;
        this.title = Title;
        return this;
    }

    ButtonsX(Large = true, Static = false, Title = ""): this {
        this.below = false;
        this.above = false;
        this.x = true;
        this.large = Large;
        this.static = Static;
        this.title = Title;
        return this;
    }

    ButtonsAbove(Large = true, Static = false, Title = ""): this {
        this.below = false;
        this.above = true;
        this.x = false;
        this.large = Large;
        this.static = Static;
        this.title = Title;
        return this;
    }

    ButtonsAll(Large = true, Static = false, Title = ""): this {
        this.below = true;
        this.above = true;
        this.x = false;
        this.large = Large;
        this.static = Static;
        this.title = Title;
        return this;
    }

    ButtonsNone(Large = true, Static = false, Title = ""): this {
        this.below = false;
        this.above = false;
        this.x = false;
        this.large = Large;
        this.static = Static;
        this.title = Title;
        return this;
    }
}

/**
 * Initializes and displays a modal.
 * @param {string} controller - The MVC controller name.
 * @param {string} action - The MVC action name.
 * @param {InCoreModalLayout} layout - The modal layout configuration.
 * @param {string | undefined} id - Optional ID for the MVC action.
 * @param {Function | undefined} successCallback - Optional callback on success.
 * @param {string | undefined} actionName - Optional custom action name.
 * @param {...any} args - Additional key-value parameters.
 */
export async function InCoreModal(
    controller: string,
    action: string,
    layout: InCoreModalLayout = new InCoreModalLayout().ButtonsBelow(),
    id: string | undefined = undefined,
    successCallback: Function | undefined = undefined,
    actionName: string | undefined = undefined,
    ...args: any[]
): Promise<void> {
    // Un'apertura è già in corso: ignora il doppio trigger.
    if (_modalOpening) return;
    _modalOpening = true;

    let url = `/${controller}/${actionName ?? action}`;
    if (id) url += `/${id}`;

    url += addParameters(args);

    try {
        const result = await $.ajax({ method: "GET", url });
        await InitModal(result, controller, action, layout, id, successCallback, actionName, args);
    } catch (error) {
        if (error instanceof Error && 'responseText' in error) {
            InCoreSwal2(InCoreSwal2Icon.Error, (error as any).responseText);
        } else {
            InCoreSwal2(InCoreSwal2Icon.Error, "An unknown error occurred.");
        }
    } finally {
        _modalOpening = false;
    }
}

function addParameters(args: any[]) {
    var url = "";
    var manipulatedArgs: any[];

    if (args !== undefined) {

        if (args.length == 1) {
            manipulatedArgs = args[0];
        }
        else {
            manipulatedArgs = args;
        }

        if (manipulatedArgs.length % 2 === 0) {
            manipulatedArgs.forEach((arg, index) => {
                if (index % 2 === 0) {
                    url += index === 0 ? "?" : "&";
                    url += `${args[index]}=${args[index + 1]}`;
                }
            });
        }
    }

    return url;
}

/**
 * Rimuove gli artefatti del modale lasciati appesi al DOM: backdrop residui e il lock
 * dello scroll su <body> (classe modal-open + overflow/padding-right inline). È la difesa
 * centrale contro lo "scroll bloccato": agisce SOLO quando non resta alcun modale aperto,
 * così non interferisce con modali ancora visibili.
 */
function cleanupModalArtifactsIfNoneOpen(): void {
    if (document.querySelector(".modal.show")) return;
    document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");
}

/**
 * Clears the modal for reuse.
 */
export function ClearModal(): void {
    const el = document.querySelector<HTMLElement>(".InCoreModal");

    $(".InCoreModal").find("*").off();

    // Dispose NATIVO dell'istanza precedente. Niente bridge jQuery .modal("dispose"):
    // mischiare l'API nativa (getOrCreateInstance) con il bridge jQuery generava istanze
    // disallineate, con backdrop duplicati e <body> che restava in modal-open (scroll
    // bloccato). Dopo il dispose ripuliamo backdrop orfani e sblocchiamo lo scroll: il
    // modale viene ricostruito e rimostrato subito dopo, e show() riapplica backdrop +
    // modal-open in modo pulito.
    if (el) {
        Modal.getInstance(el)?.dispose();
        el.classList.remove("show");
    }
    document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
    document.body.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.body.style.removeProperty("padding-right");

    $(".InCoreModalTitle").hide().text("");
    $(".InCoreModalForm").attr("asp-controller", "").attr("asp-action", "").attr("action", "");
    $(".InCoreModalBody").html("");
    $(".InCoreModalBtnOthers").html("");
    $(".InCoreModalBtnSave").text("").show();
}

/**
 * Initializes the modal with the provided content.
 * @param {any} html - The HTML content for the modal.
 * @param {string} controller - The MVC controller name.
 * @param {string} action - The MVC action name.
 * @param {InCoreModalLayout} layout - The modal layout configuration.
 * @param {string | undefined} id - Optional ID for the MVC action.
 * @param {Function | undefined} successCallback - Optional callback on success.
 * @param {string | undefined} actionName - Optional custom action name.
 * @param {...any} args - Additional key-value parameters.
 */
export async function InitModal(
    html: any,
    controller: string,
    action: string,
    layout: InCoreModalLayout = new InCoreModalLayout().ButtonsBelow(),
    id: string | undefined = undefined,
    successCallback: Function | undefined = undefined,
    actionName: string | undefined = undefined,
    args: any[]
): Promise<void> {
    ClearModal();

    const modal = Modal.getOrCreateInstance($(".InCoreModal")[0]);
    if (layout.static) {
        $(".InCoreModal").attr("data-bs-backdrop", "static").attr("data-bs-keyboard", "false");
    }

    _modalCloseCallback = successCallback;


    // Parse dynamic content from the provided HTML
    let title = "";
    let form = "";
    let table = "";
    let dl = "";
    const btnOther: HTMLElement[] = [];

    // Extract title
    $(html).find("h1, h2, h3, h4, h5, h6").each((_i, v) => {
        title += (v.innerHTML ?? v.innerText) + " ";
    });

    // Override title if provided in layout
    if (layout && layout.title && layout.title != "") {
        title = layout.title;
    }

    // Extract form
    $(html).find("form").each((_i, v) => {
        form = v.innerHTML ?? v.innerText;
    });

    // Extract table
    $(html).find("table").each((_i, v) => {
        table = v.outerHTML;
    });

    // Extract details (dl or .details)
    $(html).find("dl").each((_i, v) => {
        dl = v.outerHTML;
    });

    if (!dl) {
        $(html).find(".details").each((_i, v) => {
            dl = v.outerHTML;
        });
    }

    // Extract additional buttons
    $(html).find(":not(.modalIgnore)[type='button']").each((_i, v) => {
        btnOther.push(v as HTMLElement);
    });
    var confirm = $(html).find(".modalIgnore.swalConfirm")[0];
    var confirmClasses = confirm?.classList ? Array.from(confirm.classList).join(" "): "btn btn-success";
    var confirmText = confirm?.textContent?.trim() || "Si";
    var deny = $(html).find(".modalIgnore.swalDeny")[0];
    var denyClasses = deny?.classList ? Array.from(deny.classList).join(" "): "btn btn-danger";
    var denyText = deny?.textContent?.trim() || "No";
    var cancel = $(html).find(".modalIgnore.swalCancel")[0];
    var cancelClasses = cancel?.classList ? Array.from(cancel.classList).join(" ") : "btn btn-empty";
    var cancelText = cancel?.textContent?.trim() || "";

    // Prepare modal based on action
    switch (action) {
        case InCoreModalAction.DELETE:
            const question = $(html).find("h3").text();
            const deleteId = id ?? $(html).find("input[name='Id']").val();
            const token = $(html).find("input[name='__RequestVerificationToken']").val();
            let res = await InCoreSwal2X(InCoreSwal2Type.ConfirmDeny, InCoreSwal2Icon.Question, question, "", false, confirmText, cancelText,denyText,confirmClasses,cancelClasses,denyClasses);
            if (res.isConfirmed) {
                SaveModal(undefined, controller, action, layout, String(deleteId ?? undefined), String(token ?? undefined), successCallback, actionName, args);
            }
            return;

        case InCoreModalAction.INDEX:
            const nomeDT = "dt-modal-" + controller + "-" + (actionName ?? action);
            $(".InCoreModalTitle").html(controller.trim()).show();
            // Prepare the
            $(table).attr("id", nomeDT);
            $(table).find("a:not(.modalIgnore), button:not(.modalIgnore)").hide();
            $(".InCoreModalBody").html(table);
            $(".InCoreModalBtnSave").hide();
            break;

        case InCoreModalAction.DETAILS:
            $(".InCoreModalTitle").html(title.trim()).show();
            $(".InCoreModalBody").html(dl);
            $(".InCoreModalBtnSave").hide();
            break;

        case InCoreModalAction.CREATE:
        case InCoreModalAction.EDIT:
            $(".InCoreModalTitle").html(title.trim()).show();
            $(".InCoreModalForm").attr("asp-controller", controller).attr("asp-action", actionName ?? action).attr("action", actionName ?? action);
            $(".InCoreModalBody").html(form);
            $(".InCoreModalBody > div > [type='submit']").parent().hide();
            $(".InCoreModalBody .btn:not(.modalIgnore)").hide();
            // $(".InCoreModalBtnSave").text($(html).find("[type='submit']").val() as string).attr('value', actionName ?? action).show();
            const submit = $(html).find("[type='submit']");
            const btn = $(".InCoreModalBtnSave");
            if (submit.length > 0) {
                btn
                    .text(submit.val() as string)
                    .attr("value", actionName ?? action)
                    .show();

                ["incore-permission-type", "incore-permission-value"].forEach(attr => {
                    const val = submit.attr(attr);
                    console.log(val);
                    if (val) btn.attr(attr, val);
                });
            }
            else {
                $(".InCoreModalBtnSave").hide();
            }
            $(".InCoreModalBtnSave").on("click", (event) => {
                setTimeout(() => ValidationErrorToTooltip(), 100);
                SaveModal(event as unknown as Event, controller, action, layout, id, undefined, successCallback, actionName, args);
            });
            break;

        default:
            // Since it's a standard action, I activate only the X button to close the Modal
            layout = new InCoreModalLayout().ButtonsX();

            $(".InCoreModalTitle").html(title.trim()).show();
            $(".InCoreModalBody").html(html);
            $(".InCoreModalBtnSave").hide();
            if (successCallback) {
                $(".InCoreModal").on("hidden.bs.modal", () => successCallback());
            }
            break;
    }

    // Append additional buttons
    btnOther.forEach((btn) => {
        $(".InCoreModalBtnOthers").append(btn);
    });

    // Configure modal layout
    if (layout.above) $(".InCoreLayoutAbove").show();
    else $(".InCoreLayoutAbove").hide();

    if (layout.x) $(".InCoreLayoutX").show();
    else $(".InCoreLayoutX").hide();

    if (layout.below) $(".InCoreLayoutBelow").show();
    else $(".InCoreLayoutBelow").hide();

    if (layout.large) $(".InCoreModal > .modal-dialog").addClass("modal-lg");
    else $(".InCoreModal > .modal-dialog").removeClass("modal-lg");

    // Execute scripts in the HTML
    $(html).find("script").each((_i, v) => {
        const scriptElement = $("<script>").text($(v).text());
        $(".InCoreModal .modal-content").append(scriptElement);
    });

    // Show the modal
    modal.show();

    // Handle validation tooltips
    setTimeout(() => ValidationErrorToTooltip(), 100);
}

/**
 * Closes the modal and resets its state.
 */
export function CloseModal(shouldCallback: boolean = false): void {
    const el = document.querySelector<HTMLElement>(".InCoreModal");
    const instance = el ? Modal.getInstance(el) : null;

    if (el && instance) {
        // Chiusura tramite l'API nativa: Bootstrap gestisce l'animazione e rimuove
        // backdrop/modal-open al termine. cleanupModalArtifactsIfNoneOpen() è la rete di
        // sicurezza per eventuali residui orfani, eseguita solo a modale chiuso. Niente
        // più rimozioni sincrone di backdrop/body che andavano in race con l'animazione.
        el.addEventListener("hidden.bs.modal", () => cleanupModalArtifactsIfNoneOpen(), { once: true });
        instance.hide();
    } else {
        // Nessuna istanza viva (es. già disposta): forziamo comunque lo sblocco dello scroll.
        cleanupModalArtifactsIfNoneOpen();
    }

    if (shouldCallback && _modalCloseCallback) {
        _modalCloseCallback();
    }
}

/**
 * Closes the modal and resets its state, returning a Promise.
 */
export function CloseModalPromise(): Promise<void> {
    return new Promise<void>(resolve => {
        const modals = document.querySelectorAll<HTMLElement>(".InCoreModal.show");

        if (modals.length === 0) {
            resolve();
            return;
        }

        let closed = 0;

        modals.forEach(m => {
            const instance =
                Modal.getInstance(m) ??
                new Modal(m);

            m.addEventListener(
                "hidden.bs.modal",
                () => {
                    closed++;
                    if (closed === modals.length) {
                        resolve();
                    }
                },
                { once: true }
            );

            instance.hide();
        });
    });
}

/**
 * Reloads the page when a modal save button is clicked.
 */
export function InCoreModalLocationReload(): void {
    const checkExistInCoreModalBtnSave = setInterval(function () {
        if ($(".InCoreModalBtnSave").length) {
            clearInterval(checkExistInCoreModalBtnSave);
            $(".InCoreModalBtnSave").on("click", function (_e) {
                const checkExistSwal2Confirm = setInterval(function () {
                    if ($(".swal2-confirm").length) {
                        clearInterval(checkExistSwal2Confirm);
                        $(".swal2-confirm").on("click", function (_e) {
                            location.reload();
                        });
                    }
                }, 1000);
            });
        }
    }, 1000);
}

/**
 * Saves the modal data via AJAX.
 * @param {Event | undefined} event - The event object, if any.
 * @param {string} controller - The MVC controller name.
 * @param {string} action - The MVC action name.
 * @param {InCoreModalLayout} layout - The modal layout configuration.
 * @param {string | undefined} id - Optional ID for the MVC action.
 * @param {string | undefined} antiForgeryToken - Optional anti-forgery token.
 * @param {Function | undefined} successCallback - Optional callback on success.
 * @param {string | undefined} actionName - Optional custom action name.
 * @param {any} args - Additional key-value parameters.
 */
export function SaveModal(
    event: Event | undefined,
    controller: string,
    action: string,
    layout: InCoreModalLayout,
    id: string | undefined,
    antiForgeryToken: string | undefined,
    successCallback: Function | undefined = undefined,
    actionName: string | undefined = undefined,
    args: any[]
): void {
    // Prevent default form submission behavior
    if (event) event.preventDefault();

    // Retrieve the anti-forgery token
    const token = String(antiForgeryToken ?? $("input[name='__RequestVerificationToken']").val());

    // Construct the URL for the POST request
    let url = `/${controller}/${actionName ?? action}`;
    if (id) url += `/${id}`;

    url += addParameters(args);

    // Serialize form data
    let data = $(".InCoreModalForm").serialize();

    // Define success and error handlers
    const onSuccess = (html: any, status: string, xhr: JQueryXHR) => {
        if (html.includes("!DOCTYPE")) {
            // Redirect detected
            if (successCallback) successCallback(html);
            CloseModal();
        } else {
            // Reinitialize modal to handle validation errors
            InitModal(xhr.responseText, controller, action, layout, id, successCallback, actionName, args);
        }
    };

    const onError = (result: any) => {
        const errorMessage = result.responseJSON?.detail || "An error occurred.";
        InCoreSwal2(InCoreSwal2Icon.Error, errorMessage);
    };

    // Customize behavior based on the action
    switch (action) {
        case InCoreModalAction.DELETE:
            data = ""; // No form data for DELETE
            break;

        case InCoreModalAction.INDEX:
        case InCoreModalAction.DETAILS:
            // No save operation for INDEX or DETAILS
            return;

        case InCoreModalAction.CREATE:
        case InCoreModalAction.EDIT:
        default:
            // Default behavior for CREATE, EDIT, or other actions
            break;
    }

    // Perform the AJAX POST request
    $.ajax({
        headers: {
            RequestVerificationToken: token as string,
        },
        type: "POST",
        url: url,
        data: data,
        success: onSuccess,
        error: onError,
    });
}